import { axiosInstanceWebsite } from "../instance";

export interface MetaIntegration {
  id: number;
  owner_id: string;
  is_active: boolean;
  ad_account_id: string | null;
  business_id: string | null;
  business_name: string | null;
  expires_at: number | null;
  created_at: string;
  updated_at: string;
}

export interface MetaOAuthUrlResponse {
  authorizationUrl: string;
}

export interface MetaAdAccount {
  id: string;
  name: string;
  account_status: number;
  currency?: string;
  timezone_name?: string;
}

export interface MetaAdAccountsResponse {
  ok: boolean;
  adaccounts: MetaAdAccount[];
}

export interface MetaBusiness {
  id: string;
  name: string;
}

export interface MetaBusinessesResponse {
  ok: boolean;
  businesses: MetaBusiness[];
}

export interface CampaignMetricsResponse {
  ok: boolean;
  ad_account_id: string;
  date_preset: string;
  active_ads_count: number;
  running_campaigns: string[];
  reach: number;
  frequency: number;
  impressions: number;
  video_3s: number;
  video_50: number;
  link_clicks: number;
}

export type SemaforoStatus = "red" | "yellow" | "green";

export interface AdTableRow {
  ad_id: string;
  ad_name: string;
  campaign_name: string;
  effective_status: string;
  impressions: number;
  reach: number;
  frequency: number;
  spend: number;
  cpm: number;
  cpc: number;
  ctr: number;
  results: number;
  cost_per_result: number;
  purchases: number;
  roas: number;
  hook_rate: number;
  thruplay: number;
  budget: number | null;
  platform: string;
  destination_url: string | null;
  funnel_id: string | null;
  funnel_name: string | null;
  semaforo: SemaforoStatus;
  thumbnail_url: string | null;
  format: "video" | "image";
  product_id: string | null;
  product_name: string | null;
}

// --- Action plan types ---

export interface ActionPlanAnalysisSummary {
  headline: string;
  overall_health_score: number;
}

export interface ActionPlanCriticalBottleneck {
  title: string;
  financial_impact_text: string;
  diagnosis: string;
  action_plan: string[];
}

export interface ActionPlanWinningAsset {
  asset_name: string;
  message: string;
}

export interface ActionPlanSecondaryOptimization {
  metric: string;
  advice: string;
}

export interface ActionPlanAiResponse {
  analysis_summary: ActionPlanAnalysisSummary;
  critical_bottleneck: ActionPlanCriticalBottleneck;
  winning_assets: ActionPlanWinningAsset[];
  secondary_optimizations: ActionPlanSecondaryOptimization[];
  semaforo: Record<string, SemaforoStatus>;
}

export interface MetaActionPlan {
  id: number;
  owner_id: string;
  ad_account_id: string;
  ad_id: string;
  ad_name: string | null;
  campaign_name: string | null;
  date_start: string | null;
  date_end: string | null;
  metrics_snapshot: Record<string, unknown>;
  ai_response: ActionPlanAiResponse;
  created_at: string;
  updated_at: string;
}

export interface AdsTableResponse {
  ok: boolean;
  ad_account_id: string;
  date_start: string;
  date_end: string;
  ads: AdTableRow[];
}

export const getMetaIntegration = async (): Promise<MetaIntegration | null> => {
  const response = await axiosInstanceWebsite.get("/meta/integration");
  return response.data;
};

export const getMetaOAuthUrl = async (): Promise<MetaOAuthUrlResponse> => {
  const response = await axiosInstanceWebsite.get("/meta/oauth/url");
  return response.data;
};

export const getMetaAdAccounts = async (businessId?: string): Promise<MetaAdAccountsResponse> => {
  const response = await axiosInstanceWebsite.get("/meta/ad-accounts", {
    params: businessId ? { business_id: businessId } : undefined,
  });
  return response.data;
};

export const getMetaBusinesses = async (): Promise<MetaBusinessesResponse> => {
  const response = await axiosInstanceWebsite.get("/meta/businesses");
  return response.data;
};

export const updateMetaAdAccount = async (adAccountId: string): Promise<MetaIntegration> => {
  const response = await axiosInstanceWebsite.put("/meta/integration/ad-account", {
    ad_account_id: adAccountId,
  });
  return response.data;
};

export const getCampaignMetrics = async (
  adAccountId: string,
  dateStart?: string,
  dateEnd?: string,
  datePreset?: string
): Promise<CampaignMetricsResponse> => {
  const params: Record<string, string> = {};
  if (dateStart && dateEnd) {
    params.date_start = dateStart;
    params.date_end = dateEnd;
  } else if (datePreset) {
    params.date_preset = datePreset;
  } else {
    params.date_preset = "last_30d";
  }
  const response = await axiosInstanceWebsite.get(
    `/meta/campaigns/${adAccountId}/metrics`,
    { params }
  );
  return response.data;
};

export const getAdsTable = async (
  adAccountId: string,
  dateStart: string,
  dateEnd: string
): Promise<AdsTableResponse> => {
  const response = await axiosInstanceWebsite.get(
    `/meta/campaigns/${adAccountId}/ads`,
    { params: { date_start: dateStart, date_end: dateEnd } }
  );
  return response.data;
};

export const disconnectMetaIntegration = async (): Promise<MetaIntegration> => {
  const response = await axiosInstanceWebsite.post("/meta/integration", {
    isActive: false,
  });
  return response.data;
};

// --- Action plan endpoints ---

export const createActionPlan = async (
  adId: string,
  adAccountId: string,
  dateStart?: string,
  dateEnd?: string,
): Promise<MetaActionPlan> => {
  const body: Record<string, string> = { ad_account_id: adAccountId };
  if (dateStart) body.date_start = dateStart;
  if (dateEnd) body.date_end = dateEnd;
  const response = await axiosInstanceWebsite.post(`/meta/ads/${adId}/action-plan`, body);
  return response.data;
};

export const listActionPlans = async (adId: string): Promise<MetaActionPlan[]> => {
  const response = await axiosInstanceWebsite.get(`/meta/ads/${adId}/action-plans`);
  return response.data;
};

export const getActionPlan = async (id: number): Promise<MetaActionPlan> => {
  const response = await axiosInstanceWebsite.get(`/meta/action-plans/${id}`);
  return response.data;
};

// --- Product assignment ---

export const assignProductToAd = async (adId: string, productId: string): Promise<void> => {
  await axiosInstanceWebsite.post(`/meta/ads/${adId}/product`, { product_id: productId });
};

export const clearAdProductAssignment = async (adId: string): Promise<void> => {
  await axiosInstanceWebsite.delete(`/meta/ads/${adId}/product`);
};
